import React from "react";
import { Button, Card, CardActions, CardContent, Grid, Stack, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { ConfirmationModal } from "@packsize/components";

import { Loading } from "@/components/loading";
import type { EmServiceMachine, Machine, X5wPrintServiceMachine } from "@/schemas/machine";
import { getLocalizedMachineType } from "@/translations/utils/machinteType";
import { ConfigurationPageLayout } from "@/views/configuration/components/configuration-page-layout";

import { EmService } from "./em-service";
import { FusionService } from "./fusion-service";
import { X5wPrintService } from "./x5-w-print-service";

const EM_ESTOP_ERROR_CODE = 1;

export interface MachineServiceTemplateProps {
	readonly machineList: Machine[] | null | undefined;
	readonly isLoading: boolean;
	readonly selectedMachine: Machine | undefined;
	readonly isLeaveServiceModalOpen: boolean;
	readonly isLeaveServiceMutationPending: boolean;
	readonly onEnterServiceMode: (machine: Machine) => Promise<void>;
	readonly onSelectMachine: (machine: Machine | undefined) => void;
	readonly onOpenLeaveServiceModal: (machine: Machine) => void;
	readonly onCloseLeaveServiceModal: () => void;
	readonly onConfirmLeaveService: () => Promise<void>;
}

const isInServiceMode = (machine: Machine | undefined): boolean => {
	return Boolean(machine && (machine.status === "Service" || machine.status === "Calibrating"));
};

const canEnterServiceMode = (machine: Machine): boolean => {
	if (machine.machineType === "X5wPrint") {
		return machine.status !== "Offline";
	}

	if (machine.machineType === "Em") {
		return machine.status === "Paused" || machine.status === "ChangingCorrugate";
	}

	return machine.status === "Paused" || machine.status === "Error" || machine.status === "ChangingCorrugate";
};

const shouldDisableLeaveServiceMode = (machine: Machine | undefined): boolean => {
	if (machine && machine.machineType === "Em") {
		const emMachine = machine as EmServiceMachine;
		return emMachine.error?.code !== EM_ESTOP_ERROR_CODE;
	}

	return false;
};

export const MachineServiceTemplate: React.FC<MachineServiceTemplateProps> = ({
	machineList,
	isLoading,
	selectedMachine,
	isLeaveServiceModalOpen,
	isLeaveServiceMutationPending,
	onEnterServiceMode,
	onSelectMachine,
	onOpenLeaveServiceModal,
	onCloseLeaveServiceModal,
	onConfirmLeaveService,
}) => {
	const intl = useIntl();

	if (isLoading === true || machineList === null || machineList === undefined) {
		return (
			<ConfigurationPageLayout title={intl.formatMessage({ id: "Machine Service", defaultMessage: "Machine Service" })}>
				<Card>
					<CardContent>
						<Loading />
					</CardContent>
				</Card>
			</ConfigurationPageLayout>
		);
	}

	const selectedMachineIsInServiceMode = isInServiceMode(selectedMachine);

	if (selectedMachine && selectedMachineIsInServiceMode === false) {
		return (
			<ConfigurationPageLayout title={intl.formatMessage({ id: "Machine Service", defaultMessage: "Machine Service" })}>
				<Card>
					<CardContent>
						<Loading />
					</CardContent>
				</Card>
			</ConfigurationPageLayout>
		);
	}

	if (machineList.length === 0) {
		return (
			<ConfigurationPageLayout title={intl.formatMessage({ id: "Machine Service", defaultMessage: "Machine Service" })}>
				<Card variant="outlined" data-testid="machine-service__empty-state">
					<CardContent
						component={Grid}
						container
						justifyContent="center"
						alignItems="center"
						direction="column"
						sx={{ minHeight: 240 }}
					>
						<Typography variant="h2">
							<FormattedMessage
								id="No machines available for service commands"
								defaultMessage="No machines available for service commands"
							/>
						</Typography>
						<Typography color="text.secondary">
							<FormattedMessage
								id="The current tenant has no machines available for service commands."
								defaultMessage="The current tenant has no machines available for service commands."
							/>
						</Typography>
					</CardContent>
				</Card>
			</ConfigurationPageLayout>
		);
	}

	const title = selectedMachineIsInServiceMode
		? intl.formatMessage(
				{ id: "Machine Service: {alias}", defaultMessage: "Machine Service: {alias}" },
				{ alias: selectedMachine?.alias },
			)
		: intl.formatMessage({ id: "Machine Service", defaultMessage: "Machine Service" });

	const actions = (
		<>
			{selectedMachineIsInServiceMode && selectedMachine?.machineType !== "X5wPrint" ? (
				<Button
					variant="contained"
					onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
						event.stopPropagation();
						if (selectedMachine) {
							onOpenLeaveServiceModal(selectedMachine);
						}
					}}
				>
					<FormattedMessage id="Leave Service Mode" defaultMessage="Leave Service Mode" />
				</Button>
			) : null}
			{selectedMachine ? (
				<Button
					variant="contained"
					onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
						event.stopPropagation();
						onSelectMachine(undefined);
					}}
				>
					<FormattedMessage id="Go Back" defaultMessage="Go Back" />
				</Button>
			) : null}
		</>
	);

	return (
		<ConfigurationPageLayout
			title={title}
			actions={actions}
			sx={{ overflowY: "auto" }}
			contentSx={{ flex: "none", overflow: "visible" }}
		>
			<Card>
				<CardContent>
					{selectedMachineIsInServiceMode === true &&
						(() => {
							switch (selectedMachine?.machineType) {
								case "Em":
									return <EmService machine={selectedMachine as EmServiceMachine} />;
								case "Fusion":
									return <FusionService machine={selectedMachine} />;
								case "X5wPrint":
									return <X5wPrintService selectedMachine={selectedMachine as X5wPrintServiceMachine} />;
								default:
									return (
										<FormattedMessage
											id="Machine does not have a service mode"
											defaultMessage="Machine does not have a service mode"
										/>
									);
							}
						})()}

					{selectedMachine === undefined && (
						<Grid container spacing={2} display="grid" gridTemplateColumns="repeat(5, 1fr)">
							{machineList.map((machine) => {
								const machineIsInServiceMode = isInServiceMode(machine);
								return (
									<Card
										key={machine.id}
										sx={{
											borderColor: machineIsInServiceMode ? "warning.main" : undefined,
											cursor: machineIsInServiceMode ? "pointer" : "default",
										}}
										onClick={() => {
											if (machineIsInServiceMode) {
												onSelectMachine(machine);
											}
										}}
									>
										<CardContent sx={{ flexGrow: 1 }}>
											<Typography variant="h6" component="div" gutterBottom>
												{machine.alias}
											</Typography>
											<Typography variant="body2" color="text.secondary" gutterBottom>
												{getLocalizedMachineType(machine.machineType, intl)}
											</Typography>
											<Typography variant="body2" color="text.secondary">
												{machine.status ? (
													<FormattedMessage
														id={machine.status}
														defaultMessage="{status}"
														values={{ status: machine.status }}
													/>
												) : (
													<FormattedMessage id="Offline" defaultMessage="Offline" />
												)}
											</Typography>
										</CardContent>
										{machineIsInServiceMode === false && (
											<CardActions>
												<Button
													variant="contained"
													disabled={canEnterServiceMode(machine) === false}
													fullWidth
													onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
														event.stopPropagation();
														void onEnterServiceMode(machine);
													}}
												>
													<FormattedMessage id="Enter Service Mode" defaultMessage="Enter Service Mode" />
												</Button>
											</CardActions>
										)}
										{machineIsInServiceMode && machine.machineType !== "X5wPrint" ? (
											<CardActions>
												<Stack direction="column" spacing={1} width="100%" justifyContent="end">
													<Button
														variant="contained"
														color="success"
														fullWidth
														onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
															event.stopPropagation();
															onSelectMachine(machine);
															onOpenLeaveServiceModal(machine);
														}}
													>
														<FormattedMessage id="Leave Service Mode" defaultMessage="Leave Service Mode" />
													</Button>
													<Button
														variant="contained"
														fullWidth
														onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
															event.stopPropagation();
															void onEnterServiceMode(machine);
														}}
													>
														<FormattedMessage id="Enter Service Mode" defaultMessage="Enter Service Mode" />
													</Button>
												</Stack>
											</CardActions>
										) : null}
										{machineIsInServiceMode && machine.machineType === "X5wPrint" ? (
											<CardActions>
												<Button
													variant="contained"
													color="success"
													fullWidth
													onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
														event.stopPropagation();
														onSelectMachine(machine);
													}}
												>
													<FormattedMessage id="Enter" defaultMessage="Enter" />
												</Button>
											</CardActions>
										) : null}
									</Card>
								);
							})}
						</Grid>
					)}
				</CardContent>
			</Card>
			<ConfirmationModal
				open={isLeaveServiceModalOpen}
				title={intl.formatMessage({
					id: "Leave Service Mode?",
					defaultMessage: "Leave Service Mode?",
				})}
				confirmText={intl.formatMessage({
					id: "Leave Service Mode",
					defaultMessage: "Leave Service Mode",
				})}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				onClose={onCloseLeaveServiceModal}
				onCancel={onCloseLeaveServiceModal}
				onConfirm={onConfirmLeaveService}
				size="sm"
				slotProps={{
					confirmButton: {
						loading: isLeaveServiceMutationPending,
						disabled: shouldDisableLeaveServiceMode(selectedMachine),
						"data-testid": "leave-service-mode-modal__button__confirm",
					},
					cancelButton: {
						"data-testid": "leave-service-mode-modal__button__cancel",
					},
				}}
			>
				<Typography>
					<FormattedMessage
						id="Are you sure you want to leave service mode for {alias}?"
						defaultMessage="Are you sure you want to leave service mode for {alias}?"
						values={{ alias: selectedMachine?.alias }}
					/>
				</Typography>
				{shouldDisableLeaveServiceMode(selectedMachine) && (
					<Typography color="error" sx={{ marginTop: 2 }}>
						<FormattedMessage
							id="Place machine in E-Stop to leave service mode"
							defaultMessage="Place machine in E-Stop to leave service mode"
						/>
					</Typography>
				)}
			</ConfirmationModal>
		</ConfigurationPageLayout>
	);
};
